import { advanceWalkPhase } from '../engine/animation'
/**
 * The player: Tavi.
 *
 * This owns input interpretation, the frame-by-frame movement model, power
 * states, and the player's interactions with entities and bumpable objects. It
 * runs on the same fixed 60Hz step as everything else, and every movement
 * decision goes through `engine/physics.ts` so the feel can be tuned in one
 * place.
 */

import { TILE } from '../engine/constants'
import {
  moveX,
  moveY,
  applySlope,
  resolveDynamic,
  type DynamicSolidRect,
  type SolidQuery,
} from '../engine/collision'
import {
  PHYS,
  applyHorizontal,
  applyVertical,
  clampFall,
  jumpVelocityFor,
  newMoveState,
  profileFor,
  type MoveIntent,
  type MoveState,
} from '../engine/physics'
import { sfxHurt, sfxJump } from '../core/audio'
import type { LevelRuntime, PlayerLike } from './types'

export type Power = 'small' | 'big' | 'fire'

/** Hitbox sizes per power state. Narrower than a tile so 1-tile gaps pass. */
const HITBOX: Record<Power, { w: number; h: number }> = {
  small: { w: 11, h: 15 },
  big: { w: 12, h: 26 },
  fire: { w: 12, h: 26 },
}

/** Crouched height. One tile, so big Tavi fits through a 1-tile gap. */
const DUCK_H = 16

export interface PlayerInput {
  left: boolean
  right: boolean
  up: boolean
  down: boolean
  run: boolean
  jump: boolean
  /** True on the frame the jump key went down. */
  jumpPressed: boolean
}

export class Player implements PlayerLike {
  x = 0
  y = 0
  w = HITBOX.small.w
  h = HITBOX.small.h
  vx = 0
  vy = 0
  facing: 1 | -1 = 1
  power: 'small' | 'big' = 'small'
  alive = true

  /** Wider power state including fire. */
  state: Power = 'small'
  onGround = false
  onCeiling = false
  onWall: 1 | -1 | 0 = 0
  ducking = false

  /** Movement bookkeeping owned by the physics model. */
  readonly move: MoveState = newMoveState()

  /** Frames of invulnerability after taking damage. */
  invuln = 0
  /** Frames of shield power remaining. */
  shieldTime = 0
  /** Cooldown between fireballs, in frames. */
  private fireCooldown = 0
  /** Set for one frame when landing, to trigger a squash. */
  justLanded = false
  /** True when the player is actively climbing a vine. */
  climbing = false
  /** Set true when the player walks off a ledge, cleared when they jump. */
  jumpedThisFrame = false
  /** Tile coords of the last solid ground the player stood on. */
  onVine = false

  private level: LevelRuntime | null = null
  private ignoreOneWayFrames = 0
  /**
   * Travelling solids for this frame, published by the level before the player
   * steps. Empty for a level with no moving platforms, so the common case costs
   * one length check.
   */
  private dyn: DynamicSolidRect[] = []
  /**
   * Frames during which the ceiling is ignored, opened by a head bump that
   * launched the player. Without this a note block's `setVy(-9)` is cancelled
   * on the very next step by the ceiling still being there, pinning Tavi
   * underneath it.
   */
  private ceilingIgnore = 0
  /** Frames left of a pending stand-up headroom recheck. */
  private standCheck = 0

  /** Attach to a level so bump events can be dispatched. */
  bind(level: LevelRuntime): void {
    this.level = level
  }

  /**
   * Publish the level's travelling solids for the coming step.
   *
   * Called by the level before `player.update`, because the platform moves in
   * `level.step` and the player has to be resolved against where it is now.
   */
  setDynamicSolids(rects: DynamicSolidRect[]): void {
    this.dyn = rects
  }

  private dynamicSolids(): DynamicSolidRect[] {
    return this.dyn
  }

  /** Place the player at a world position, resetting velocity. */
  placeAt(x: number, y: number): void {
    this.setPower('small')
    this.x = x
    this.y = y
    this.vx = 0
    this.vy = 0
    this.alive = true
    this.onGround = false
    this.invuln = 0
    this.shieldTime = 0
    this.ceilingIgnore = 0
    this.onVine = false
    this.climbing = false
    this.onCeiling = false
    this.onWall = 0
    this.justLanded = false
    this.jumpedThisFrame = false
    this.ignoreOneWayFrames = 0
    this.standCheck = 0
    this.fireCooldown = 0
    this.dyn = []
    this.facing = 1
    Object.assign(this.move, newMoveState())
    for (const key of Object.keys(this.input) as (keyof PlayerInput)[])
      this.input[key] = false
  }

  get intent(): MoveIntent {
    return {
      dir: (this.input.right ? 1 : 0) - (this.input.left ? 1 : 0),
      run: this.input.run,
      jump: this.input.jump,
      jumpPressed: this.input.jumpPressed,
      down: this.input.down,
    }
  }

  input: PlayerInput = {
    left: false,
    right: false,
    up: false,
    down: false,
    run: false,
    jump: false,
    jumpPressed: false,
  }

  /** One simulation step. Call before `level.step()` resolves collisions. */
  update(world: SolidQuery, level: LevelRuntime): void {
    this.level = level
    // Grip is a property of the surface under the feet, so it is re-read every
    // frame rather than latched: the transition onto and off the ice is the
    // entire mechanic, and a cached flag would leave Tavi skating for a frame
    // after he has stepped off it. Resolving it from the feet also means the
    // ice profile cannot follow him into the air — one frame after leaving the
    // floor the tile under him is air and he is back on the normal profile.
    const p = profileFor(
      this.state === 'small' ? 'small' : 'big',
      this.standingOnIce(world),
    )
    const intent = this.intent

    if (this.invuln > 0) this.invuln--
    if (this.shieldTime > 0) this.shieldTime--
    if (this.fireCooldown > 0) this.fireCooldown--
    if (this.ignoreOneWayFrames > 0) this.ignoreOneWayFrames--

    this.climbing = this.onVine && (this.input.up || this.input.down)

    if (this.climbing) {
      this.updateClimbing()
      return
    }

    // Ducking: big Tavi shrinks to a low profile and cannot move.
    const wantDuck = this.input.down && this.state !== 'small' && this.onGround
    this.setDuck(wantDuck)

    // ── horizontal ───────────────────────────────────────────────────────
    // The direction is gated on the *settled* crouch rather than on `wantDuck`,
    // so Tavi keeps his footing on the frame the crouch starts instead of
    // coasting one more frame on the momentum he had while standing — and,
    // symmetrically, cannot resume moving on the frame he stands back up.
    const effective: MoveIntent = this.ducking
      ? { ...intent, dir: 0, run: false }
      : intent
    this.vx = applyHorizontal(this.move, this.vx, this.onGround, effective, p)
    if (effective.dir !== 0) this.facing = effective.dir as 1 | -1

    // ── jump: coyote time and input buffering ───────────────────────────
    if (intent.jumpPressed) this.move.jumpBuffer = PHYS.jumpBuffer
    if (this.move.jumpBuffer > 0) this.move.jumpBuffer--
    if (this.onGround) this.move.coyote = PHYS.coyoteTime
    else if (this.move.coyote > 0) this.move.coyote--

    // Drop through a one-way platform: down + jump.
    if (
      intent.jumpPressed &&
      intent.down &&
      this.onGround &&
      this.onOneWay(world)
    ) {
      this.ignoreOneWayFrames = 8
      this.y += 2
      this.move.jumpBuffer = 0
    } else if (
      this.move.jumpBuffer > 0 &&
      (this.onGround || this.move.coyote > 0) &&
      !wantDuck
    ) {
      this.vy = jumpVelocityFor(this.vx, p)
      this.move.rising = true
      this.move.jumpBuffer = 0
      this.move.coyote = 0
      this.jumpedThisFrame = true
      sfxJump()
      // Run + jump throws a fireball, as in the originals, so the throw hangs
      // off the jump itself rather than off a separate later test. `onGround` is
      // read *here*, before integration: a jump press sets an upward velocity,
      // so by the time the move has been resolved the flag is already false on
      // exactly the one frame the input arrives — which is why a post-move test
      // could never be true and no fireball was ever thrown. `coyote > 0` alone
      // is not enough for a throw: a jump taken off a ledge is not a jump taken
      // on the ground, and this is the ground throw.
      if (
        this.onGround &&
        this.state === 'fire' &&
        this.input.run &&
        this.fireCooldown === 0
      ) {
        this.throwFireball(level)
      }
    } else {
      this.jumpedThisFrame = false
    }

    // ── vertical ─────────────────────────────────────────────────────────
    this.vy = applyVertical(this.move, this.vy, effective, p)
    this.vy = clampFall(this.vy)

    // ── integrate, axis separated ────────────────────────────────────────
    const wasOnGround = this.onGround
    const previousX = this.x
    this.onGround = false
    this.onCeiling = false
    this.onWall = 0

    const body = {
      x: this.x,
      y: this.y,
      w: this.w,
      h: this.h,
      vx: this.vx,
      vy: this.vy,
      onGround: false,
      onCeiling: false,
      onWall: 0 as 1 | -1 | 0,
      ignoreOneWay: this.ignoreOneWayFrames > 0,
    }

    // Pass-through window. A head bump is detected *after* the ceiling has
    // already zeroed vy, so any launch the bump sets (a note block, a spring
    // below, a bumped block that gives height) would be cancelled on the very
    // next step by the ceiling still being there. The original games solve this
    // by ignoring the ceiling for a few frames after a bump; so do we.
    //
    // `dy` is ignored for the duration, so the player rises straight through
    // the block it just hit and is free of it by the time the window closes.
    if (this.ceilingIgnore > 0) {
      this.ceilingIgnore--
      body.y += this.vy
      this.y = body.y
      this.onWall = body.onWall
      this.onCeiling = false
      this.onGround = body.onGround
      if (this.standCheck > 0) this.standCheck--
      this.jumpedThisFrame = false
      this.onVine = this.checkVine(level)
      return
    }

    moveX(body, world, this.vx)
    moveY(body, world, this.vy)

    // Travelling platforms are not on the tile grid, so they get a separate
    // corrective pass. See `resolveDynamic` for why. The pass only ever adds a
    // resting contact: clearing `onGround` first made every level that contains
    // a platform report "not grounded" *everywhere else*, because this branch
    // runs whether or not the body is anywhere near a platform. Tavi could not
    // jump at all in Pipe Dreams or Star Ride, and nothing said so.
    const dyn = this.dynamicSolids()
    if (dyn.length > 0) {
      const resting = resolveDynamic(body, dyn)
      if (resting > 0) body.onGround = true
    }

    // Position *and* velocity come back out of the body, because the body is
    // where collision is resolved. Copying the position alone discarded the
    // resolver's verdict on the velocity, which had two consequences: a
    // standing player accumulated fall speed forever (0.109, 0.219, 0.328 …
    // while never moving), and a jump carried straight on *through* any solid
    // tile the head met, because the zeroed `vy` was thrown away and the ascent
    // resumed on the next frame.
    //
    // The second one is what made the pass-through gate below misfire: it opens
    // on `this.vy < 0` to mean "the bump launched us", which only works if a
    // plain graze has already zeroed `vy` by this point — as the comment there
    // says it does. `EntityBase.physicsStep` copies `b.vx`/`b.vy` back out for
    // the same reason.
    this.x = body.x
    this.y = body.y
    this.vx = body.vx
    this.vy = body.vy
    this.onWall = body.onWall
    this.onCeiling = body.onCeiling
    this.onGround = body.onGround
    applySlope(body, world)
    this.x = body.x
    this.y = body.y
    this.vx = body.vx
    this.vy = body.vy

    this.move.animTimer = advanceWalkPhase(
      this.move.animTimer,
      this.x - previousX,
      this.onGround && !this.ducking && !this.move.skidding,
    )

    this.justLanded = this.onGround && !wasOnGround && !this.jumpedThisFrame
    if (this.justLanded) this.move.squash = PHYS.landSquashFrames
    if (this.move.squash > 0) this.move.squash--

    // ── head bump ────────────────────────────────────────────────────────
    if (this.onCeiling) {
      this.dispatchHeadBump(level)
      // Only open the pass-through window if the bump actually launched us.
      // Opening it on every ceiling graze would let Tavi drift through blocks
      // he never hit.
      if (this.vy < 0) this.ceilingIgnore = PHYS.bumpPassThrough
    }

    // ── climb state ──────────────────────────────────────────────────────
    this.onVine = this.checkVine(level)
  }

  /**
   * Spawn a fireball from the player's hand and start the throw cooldown.
   *
   * Called from the jump branch of `update`, which is the *only* reachable
   * trigger: see the comment there. The cooldown is what keeps a held jump from
   * throwing one fireball per frame while the player is still on the ground.
   */
  private throwFireball(level: LevelRuntime): void {
    level.spawn(
      'fireball',
      this.facing > 0 ? this.x + this.w : this.x - 8,
      this.y + 8,
      // The heading is a prop, not a derived value: without it the factory
      // defaults to facing right, so a leftward throw used to place the fireball
      // on the correct side and then send it back across the player.
      // `friendly` is what keeps a ricochet from hurting the thrower.
      { facing: this.facing, friendly: true },
    )
    this.fireCooldown = 18
  }

  /** True if the tile directly under the feet is a one-way platform. */
  private onOneWay(world: SolidQuery): boolean {
    const ty = Math.floor((this.y + this.h) / TILE)
    const tx0 = Math.floor(this.x / TILE)
    const tx1 = Math.floor((this.x + this.w - 1) / TILE)
    for (let tx = tx0; tx <= tx1; tx++) if (world.isOneWay(tx, ty)) return true
    return false
  }

  /**
   * True if any tile under the feet is slippery, so the movement profile
   * switches to `ICE_MOVE` for this frame.
   *
   * Same probe shape as `onOneWay`, and the same "one foot is enough"
   * generosity: the row the feet rest on, across the player's whole width, so
   * straddling the edge of an ice run already counts. The feet are used rather
   * than the centre because that is the row the collision resolver snapped them
   * to, which is what makes the answer exact rather than a pixel of guesswork.
   */
  standingOnIce(world: SolidQuery): boolean {
    const ty = Math.floor((this.y + this.h) / TILE)
    const tx0 = Math.floor(this.x / TILE)
    const tx1 = Math.floor((this.x + this.w - 1) / TILE)
    for (let tx = tx0; tx <= tx1; tx++) {
      if (world.isIce?.(tx, ty) === true) return true
    }
    return false
  }

  /** Find a bumpable object directly above the head and notify it. */
  private dispatchHeadBump(level: LevelRuntime): void {
    const ty = Math.floor((this.y - 1) / TILE)
    const tx0 = Math.floor(this.x / TILE)
    const tx1 = Math.floor((this.x + this.w - 1) / TILE)
    for (let tx = tx0; tx <= tx1; tx++) {
      const obj = level.objectAt(tx, ty)
      if (obj !== null) {
        obj.behaviour?.onHeadBump?.({
          tick: 0,
          level,
          player: this,
          state: obj.state,
        })
        return
      }
    }
  }

  /** True if standing on a vine. */
  private checkVine(level: LevelRuntime): boolean {
    const tx = Math.floor((this.x + this.w / 2) / TILE)
    const ty = Math.floor((this.y + this.h / 2) / TILE)
    const obj = level.objectAt(tx, ty)
    if (obj === null) return false
    if (obj.state.climbing === true) return true
    const g = level as unknown as {
      objects?: { def: { id: string }; state: Record<string, unknown> }[]
    }
    const match = g.objects?.find(
      (o) => o.def.id === 'vine' && o.state.climbing === true,
    )
    return match !== undefined
  }

  /** Vine movement: vertical only, no gravity. */
  private updateClimbing(): void {
    this.vx = 0
    this.vy = 0
    if (this.input.up) this.y -= 1.2
    else if (this.input.down) this.y += 1.2
    this.jumpedThisFrame = false
  }

  /**
   * Change the crouched hitbox, keeping the feet planted either way.
   *
   * The feet are the anchor, not the head. A hitbox is a top-left corner plus a
   * size, so shrinking the height without moving `y` lifts Tavi off the floor
   * by the 10px that were removed. `onGround` is then false on the next frame,
   * so `wantDuck` goes false, he stands back up, his box sinks into the floor,
   * `moveY` shoves him out again — and the whole cycle repeats at 30Hz. Duck
   * never held and `animState` alternated between `duck` and something else.
   * Moving `y` by the height difference makes ducking a stable state, and it is
   * the same invariant `setPower` keeps when it grows a small Tavi upward.
   */
  private setDuck(want: boolean): void {
    if (want === this.ducking) return
    const was = this.h
    if (want) {
      this.ducking = true
      this.h = DUCK_H
    } else {
      // Only stand up if the headroom is there, and check the whole band the
      // head will enter rather than the row it happens to be in: the extra
      // 10px cross a tile boundary about as often as not, and a head that
      // clips the ceiling leaves Tavi stuck at a height he cannot reach out of.
      // The rise is measured from the *destination* height — the crouched one is
      // what `was` already is, so `was - DUCK_H` is always zero and the check
      // would never refuse anything.
      const stand = HITBOX[this.state].h
      const world = this.level as unknown as SolidQuery | null
      if (world !== null && !this.headroom(world, stand - DUCK_H)) return
      this.ducking = false
      this.h = stand
    }
    this.y += was - this.h
  }

  /**
   * Is there clear space for the player's head to rise `rise` pixels, across
   * their whole width? Rows outside the course report as clear, so a player at
   * the top of the level can always stand up.
   */
  private headroom(world: SolidQuery, rise: number): boolean {
    if (rise <= 0) return true
    const tx0 = Math.floor(this.x / TILE)
    const tx1 = Math.floor((this.x + this.w - 1) / TILE)
    const ty0 = Math.floor((this.y - rise) / TILE)
    const ty1 = Math.floor((this.y - 1) / TILE)
    for (let tx = tx0; tx <= tx1; tx++) {
      for (let ty = ty0; ty <= ty1; ty++) {
        if (world.isSolid(tx, ty)) return false
      }
    }
    return true
  }

  // ── PlayerLike interface ───────────────────────────────────────────────

  setVy(v: number): void {
    this.vy = v
  }

  grow(): void {
    this.setPower('big')
  }

  kill(): void {
    this.alive = false
  }

  /** Take a hit. Small Tavi dies; big Tavi shrinks with brief invulnerability. */
  hurt(): void {
    if (this.shieldTime > 0) return
    if (this.invuln > 0) return
    if (this.state === 'small') {
      this.kill()
      return
    }
    this.setPower('small')
    this.invuln = 120
    sfxHurt()
  }

  /** Change power state, adjusting the hitbox while keeping the feet planted. */
  setPower(next: Power): void {
    this.state = next
    // `power` is the PlayerLike contract field; `state` additionally tracks
    // 'fire'. Behaviours read `power` (a brick only breaks for a big Tavi), so
    // leaving it pinned to 'small' silently disabled every big-Tavi behaviour.
    this.power = next === 'small' ? 'small' : 'big'
    const size = HITBOX[next]
    // Grow upward from the feet, so the player never sinks into the floor.
    this.y += this.h - size.h
    this.w = size.w
    this.h = this.ducking && next !== 'small' ? DUCK_H : size.h
    // A hitbox change can uncover or newly overlap the ceiling; recompute the
    // standing state so the next step does not use a stale flag.
    this.standCheck = 2
  }

  /** Make the player fire-capable (from an ember cell). */
  makeFire(): void {
    if (this.state === 'small') this.grow()
    this.setPower('fire')
  }

  /** Brief invincibility from a prism. */
  grantShield(frames: number): void {
    this.shieldTime = frames
  }

  get isShielded(): boolean {
    return this.shieldTime > 0
  }

  get isInvulnerable(): boolean {
    return this.shieldTime > 0 || this.invuln > 0
  }

  /** Bounce off a stomped enemy. Big Tavi bounces higher. */
  bounce(): void {
    this.vy =
      this.state === 'small' ? PHYS.stompBounce : PHYS.stompBounce * 1.25
    this.move.rising = this.vy < 0
  }

  /** Animation state name for the renderer. */
  get animState(): string {
    if (this.climbing) return 'climb'
    if (!this.onGround) return 'jump'
    if (this.ducking) return 'duck'
    if (this.move.skidding) return 'skid'
    if (
      Math.abs(this.vx) >
      profileFor(this.state === 'small' ? 'small' : 'big').walkSpeed + 0.1
    )
      return 'run'
    if (Math.abs(this.vx) > 0.1) return 'walk'
    return 'idle'
  }

  /** World-space rectangle, for entity overlap tests. */
  get rect(): { x: number; y: number; w: number; h: number } {
    return { x: this.x, y: this.y, w: this.w, h: this.h }
  }
}

/** True if two rectangles overlap. Shared by player and entity code. */
export function rectsOverlap(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
): boolean {
  return (
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  )
}
